/**
 * AppointmentsPage.tsx
 *
 * Practice-wide appointments list — view, filter, and manage appointments.
 * For calendar/schedule view, use the Schedule page.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AppointmentDetailModal } from '@/pages/appointments/AppointmentDetailModal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarDays,
  CalendarPlus,
  Download,
  Megaphone,
} from 'lucide-react';
import {
  appointmentsService,
  type AppointmentStatus,
  type Appointment,
} from '@/services/appointments.service';
import { listFacilities } from '@/services/admin.service';
import { useAppointmentFilterOptions } from '@/hooks/filters';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { useLocalDashboardFilters, useFacilityOverrideValues } from '@/pages/dashboard/dashboardFilters';
import { buildApiParams, buildMarketingFilterConditions, extractSearch } from '@/lib/filterRegistry';
import { MarketingActionsModal } from '@/components/marketing/MarketingActionsModal';
import { useAuth } from '@/hooks/useAuth';
import { useDebounce } from '@/hooks/useDebounce';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { getTimezoneAbbreviation, toLocaleDateStringInTz, toLocaleTimeStringInTz } from '@/lib/facilityTimezone';
import { usePageContext } from '@/hooks/usePageContext';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import type { FilterValue } from '@/types/filters.types';

// ─── Filter configs (shared -- single source of truth) ──────────────────────

import { APPOINTMENT_FILTER_CONFIGS } from './appointmentsShared';

const FILTER_CONFIGS = APPOINTMENT_FILTER_CONFIGS;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<AppointmentStatus, string> = {
  scheduled: 'bg-blue-100 text-blue-700',
  confirmed: 'bg-green-100 text-green-700',
  completed: 'bg-slate-100 text-slate-600',
  no_show: 'bg-orange-100 text-orange-700',
  cancelled: 'bg-red-100 text-red-700',
  checked_in: 'bg-teal-100 text-teal-700',
  contact_attempted: 'bg-amber-100 text-amber-700',
  waiting_list: 'bg-indigo-100 text-indigo-700',
  reschedule_pending: 'bg-yellow-100 text-yellow-700',
  checked_back: 'bg-cyan-100 text-cyan-700',
  action_required: 'bg-rose-100 text-rose-700',
};

/** Human-readable labels for statuses that contain underscores. */
const STATUS_LABELS: Partial<Record<AppointmentStatus, string>> = {
  no_show: 'No Show',
  contact_attempted: 'Contact Attempted',
  waiting_list: 'Waiting List',
  reschedule_pending: 'Reschedule Pending',
  checked_in: 'Checked In',
  checked_back: 'Checked Back',
  action_required: 'Action Required',
};

/** Map status to display label and style key. The API now returns the
 *  correct hierarchical status directly (matching legacy precedence). */
function getDisplayStatus(appt: Appointment): { label: string; styleKey: AppointmentStatus } {
  const label = STATUS_LABELS[appt.status] ?? appt.status.charAt(0).toUpperCase() + appt.status.slice(1);
  return { label, styleKey: appt.status };
}


// ─── Component ────────────────────────────────────────────────────────────────

export function AppointmentsPage() {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const updateAIContext = usePageContext('Appointments');

  // ── Filter state (localStorage-persisted) ────────────────────────────────
  const filterState = useLocalDashboardFilters(FILTER_CONFIGS, 'appointments');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filterState.values);

  const apiFilters = useMemo(
    () => buildApiParams(effectiveValues, filterState.negations),
    [effectiveValues, filterState.negations],
  );

  const searchValue = extractSearch(effectiveValues);
  const debouncedSearch = useDebounce(searchValue, 300);

  const [page, setPage] = useState(1);
  const [marketingModalOpen, setMarketingModalOpen] = useState(false);
  const { exporting, startExport, exportModal } = useAsyncExport({ entityType: 'appointments', entityLabel: 'appointments' });
  const perPage = 100;

  const [sortKey, setSortKey] = useState<string>('startTime');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const marketingFilterConditions = useMemo(
    () => buildMarketingFilterConditions('appointment', effectiveValues, filterState.negations),
    [effectiveValues, filterState.negations],
  );

  // Reset page on any filter or sort change
  useEffect(() => {
    setPage(1);
  }, [apiFilters, debouncedSearch, sortKey, sortDir]);

  const handleSortChange = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortKey(key);
    setSortDir(direction);
  }, []);

  // ── Detail modal state ────────────────────────────────────────────────────
  const [detailAppointment, setDetailAppointment] = useState<Appointment | null>(null);

  async function handleRowClick(appt: Appointment) {
    const full = await appointmentsService.getAppointment(appt.appointmentId);
    setDetailAppointment(full);
  }

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['appointments-list', apiFilters, page, sortKey, sortDir],
    queryFn: () =>
      appointmentsService.getAppointments(apiFilters, {
        page,
        pageSize: perPage,
        sortBy: sortKey,
        sortDir,
      }),
    staleTime: 30_000,
  });

  const { data: facilities } = useQuery({
    queryKey: ['facilities'],
    queryFn: () => listFacilities(),
    staleTime: 5 * 60_000,
  });

  const { data: appointmentTypes } = useQuery({
    queryKey: ['appointmentTypes'],
    queryFn: () => appointmentsService.getAppointmentTypes(),
    staleTime: 5 * 60_000,
  });

  // Kept as a bare call, not a deleted statement: useQuery is a hook, so
  // removing it would change hook order and stop warming this cache. Its
  // result was no longer read, so there is nothing to destructure.
  useQuery({
    queryKey: ['appointment-resources'],
    queryFn: () => appointmentsService.getResources(),
    staleTime: 5 * 60_000,
  });

  // ── Derived data ──────────────────────────────────────────────────────────
  const appointments = data?.data ?? [];
  const meta = data?.meta;
  const totalPages = meta ? Math.ceil((meta.total ?? 0) / perPage) : 1;

  const filteredAppointments = useMemo(() => {
    if (!debouncedSearch.trim()) return appointments;
    const q = debouncedSearch.toLowerCase().trim();
    return appointments.filter(
      (a) =>
        a.patientName?.toLowerCase().includes(q) ||
        a.name?.toLowerCase().includes(q) ||
        a.patientDisplayId?.toLowerCase().includes(q),
    );
  }, [appointments, debouncedSearch]);

  function handleExport() {
    startExport(apiFilters);
  }

  // Each row's label is taken at its own date: a facility's zone reads PDT in
  // October and PST after the November change, whatever today is.
  const facilityZones = useMemo(() => {
    const map = new Map<number, string>();
    for (const f of facilities ?? []) {
      if (f.ianaTimezone) {
        map.set(f.facilityId, f.ianaTimezone);
      }
    }
    return map;
  }, [facilities]);

  const typeOptions = useMemo(
    () =>
      (appointmentTypes ?? [])
        .filter((t) => t.appointmentTypeGroupActive !== false)
        .map((t) => ({
          label: t.appointmentTypeName,
          value: t.appointmentTypeId,
        })),
    [appointmentTypes],
  );

  // ── Sidebar filter plumbing ───────────────────────────────────────────────

  const sidebarOptions = useAppointmentFilterOptions();

  // ── Appointment-type merge-by-name plumbing ──────────────────────────
  // Multiple appointment types can share a name. The sidebar shows one
  // checkbox per unique name; stored filter state keeps individual IDs.

  const typeNameToIds = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const t of typeOptions) {
      const key = t.label.toLowerCase();
      const ids = map.get(key) ?? [];
      ids.push(String(t.value));
      map.set(key, ids);
    }
    return map;
  }, [typeOptions]);

  const typeIdToRepresentative = useMemo(() => {
    const map = new Map<string, string>();
    for (const [, ids] of typeNameToIds) {
      const rep = ids[0];
      for (const id of ids) map.set(id, rep);
    }
    return map;
  }, [typeNameToIds]);

  const sidebarValues = useMemo<Record<string, FilterValue>>(() => {
    const raw = effectiveValues['appt-type'];
    if (!Array.isArray(raw) || raw.length === 0) return effectiveValues;
    const mapped = new Set<string>();
    for (const id of raw) {
      const rep = typeIdToRepresentative.get(id);
      if (rep) mapped.add(rep);
    }
    return { ...effectiveValues, 'appt-type': [...mapped] };
  }, [effectiveValues, typeIdToRepresentative]);

  const handleFilterChange = useCallback(
    (key: string, value: FilterValue) => {
      if (key !== 'appt-type' || !Array.isArray(value)) {
        filterState.setFilter(key, value);
        return;
      }
      const prevReps = new Set<string>();
      const rawPrev = effectiveValues['appt-type'];
      if (Array.isArray(rawPrev)) {
        for (const id of rawPrev) {
          const rep = typeIdToRepresentative.get(id);
          if (rep) prevReps.add(rep);
        }
      }
      const nextReps = new Set(value);
      const expanded: string[] = [];
      for (const rep of nextReps) {
        const name = typeOptions.find((t) => String(t.value) === rep)?.label;
        if (name) {
          const ids = typeNameToIds.get(name.toLowerCase()) ?? [rep];
          expanded.push(...ids);
        } else {
          expanded.push(rep);
        }
      }
      filterState.setFilter('appt-type', expanded);
    },
    [filterState, effectiveValues, typeIdToRepresentative, typeNameToIds, typeOptions],
  );

  // ── AI Page Context ──────────────────────────────────────────────────
  const activeFilters = useMemo(
    () =>
      Object.entries(effectiveValues)
        .filter(([key, val]) => {
          if (key === 'search') return false;
          if (Array.isArray(val)) return val.length > 0;
          if (typeof val === 'object' && val !== null && 'from' in val) return !!(val.from || val.to);
          return false;
        })
        .map(([key]) => key)
        .join(', '),
    [effectiveValues],
  );

  useEffect(() => {
    const parts: string[] = [];
    if (activeFilters) parts.push(activeFilters);
    if (debouncedSearch) parts.push(`search="${debouncedSearch}"`);

    updateAIContext({
      filters: parts.length ? parts.join(', ') : 'none (showing all)',
      dataSummary: `${filteredAppointments.length} shown of ${meta?.total ?? 0} total, page ${page}/${totalPages}`,
    });
  }, [activeFilters, debouncedSearch, filteredAppointments.length, meta?.total, page, totalPages, updateAIContext]);

  // ── Mutations ─────────────────────────────────────────────────────────────
  const invalidateList = () => queryClient.invalidateQueries({ queryKey: ['appointments-list'] });

  // ── DataTable columns ─────────────────────────────────────────────────────

  const columns = useMemo<DataTableColumn<Appointment>[]>(() => [
    {
      key: 'startTime',
      header: 'Date & Time',
      sortable: true,
      width: 'w-[120px]',
      render: (appt) => {
        if (!appt.startTime) return <span className="text-slate-400">—</span>;
        const tz = appt.facilityIanaTimezone ?? 'America/Los_Angeles';
        const d = new Date(appt.startTime);
        const datePart = toLocaleDateStringInTz(d, 'en-US', {
          timeZone: tz, month: 'short', day: 'numeric', year: 'numeric',
        });
        const timePart = toLocaleTimeStringInTz(d, 'en-US', {
          timeZone: tz, hour: 'numeric', minute: '2-digit',
        });
        const facilityZone = appt.facilityId ? facilityZones.get(appt.facilityId) : undefined;
        const tzAbbr = facilityZone ? getTimezoneAbbreviation(facilityZone, d) : '';
        return (
          <div className="whitespace-nowrap">
            <div className="text-xs font-medium text-slate-800">{datePart}</div>
            <div className="text-[11px] leading-4 text-slate-500">{timePart} {tzAbbr}</div>
          </div>
        );
      },
    },
    {
      // Headerless member marker — sits immediately left of the patient name.
      key: 'patientIsMember',
      header: '',
      // `cn` is twMerge-backed, so the narrower padding here wins over the
      // DataTable's default px-4 and keeps the badge tight against the name.
      width: 'w-9 px-2',
      align: 'center',
      render: (appt) =>
        appt.patientIsMember ? (
          <span
            title="Member"
            aria-label="Member"
            className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-[11px] font-semibold lowercase text-green-700 ring-1 ring-green-200"
          >
            m
          </span>
        ) : null,
    },
    {
      key: 'patientName',
      header: 'Patient',
      sortable: true,
      render: (appt) => (
        <span className="font-medium text-slate-900">
          {appt.patientName ?? appt.name ?? '—'}
        </span>
      ),
    },
    {
      key: 'appointmentTypeName',
      header: 'Type',
      sortable: true,
      render: (appt) =>
        appt.appointmentTypeName ? (
          <span className="text-xs text-slate-700">
            <span
              className="inline-block h-2 w-2 rounded-full mr-1.5 align-middle"
              style={{ backgroundColor: appt.appointmentTypeColor ?? '#94a3b8' }}
            />
            {appt.appointmentTypeName}
            {/* "First Time" is the manual first-of-this-type switch; "First Visit"
                is derived server-side — the patient's first appointment ever. */}
            {appt.firstAppointment && (
              <span className="ml-1 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                First Time
              </span>
            )}
            {appt.firstVisit && (
              <span className="ml-1 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                First Visit
              </span>
            )}
            {appt.compAppointment && (
              <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                Comp
              </span>
            )}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      width: 'w-[130px]',
      render: (appt) => {
        if (appt.actionRequired) {
          return (
            <span
              title="Please open for more details"
              className={cn(
                'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium',
                STATUS_STYLES.action_required,
              )}
            >
              Action Required
            </span>
          );
        }
        const displayStatus = getDisplayStatus(appt);
        return (
          <span
            className={cn(
              'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium capitalize',
              STATUS_STYLES[displayStatus.styleKey],
            )}
          >
            {displayStatus.label}
          </span>
        );
      },
    },
    {
      key: 'facilityName',
      header: 'Facility',
      sortable: true,
      render: (appt) => <span className="text-xs text-slate-600">{appt.facilityName ?? '—'}</span>,
    },
    {
      key: 'resources',
      header: 'Resources',
      render: (appt) =>
        appt.resources && appt.resources.length > 0
          ? appt.resources.map((r) => (
              <div key={r.resourceId} className="text-xs">
                <span className="font-medium">{r.resourceName}</span>
                <span className="text-slate-400 ml-1">({r.resourceType})</span>
              </div>
            ))
          : <span className="text-slate-400">—</span>,
    },
    {
      key: 'duration',
      header: 'Duration',
      sortable: true,
      render: (appt) => (
        <span className="text-slate-600">{appt.duration ? `${appt.duration} min` : '—'}</span>
      ),
    },
  ], [facilityZones]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={FILTER_CONFIGS}
            values={sidebarValues}
            options={sidebarOptions}
            negations={filterState.negations}
            onChange={handleFilterChange}
            onNegateChange={filterState.setNegation}
            onClear={filterState.clearFilter}
            searchCommitMode="blur-or-enter"
            showAIAssistant={false}
            storageKey="appointments"
            lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          />
        )}
      >
        {/* SCRUM-1449. Two separate reports, two tokens.
       `max-md:overflow-x-hidden`: the `p-6` header block sits outside the table's own
       `overflow-x-hidden` below, and `ListPageLayout`'s content column releases its clip
       below `md` (`max-md:overflow-visible`) so the page scrolls as a document — an
       over-wide header therefore shifted the whole document sideways, taking the Filters
       button, the sort row and the list rows with it.

       LeadsPage carries this clip UNPREFIXED. Matching it exactly was the first instinct and
       the wrong one: an unprefixed clip on a page root reaches desktop, where it could hide a
       horizontal scrollbar something wide legitimately needs. The emitted-CSS check flagged it,
       and the report is a phone report, so the prefix is what the fix actually needs. Worth
       revisiting LeadsPage's spelling separately.
       `max-md:h-auto`: `h-full` against an auto-height ancestor is what left DataTable's
       `shrink-0` footer and last row under the browser chrome. */}
        <div className="flex h-full min-h-0 flex-col max-md:overflow-x-hidden max-md:h-auto max-md:pb-16">
          <div className="shrink-0 space-y-6 p-6">
            <PageHeader
              variant="compact"
              title="Appointments"
              actions={(
                /* PageHeader's action slot is already
                   `flex flex-wrap … md:flex-nowrap`, but it receives ONE child — this row —
                   and a flex container with a single item has nothing to wrap. So the four
                   buttons stayed on one line and "New Appointment" ran off the right edge.
                   The wrap escape has to be here, on the row that actually holds them. */
                <div className="flex items-center gap-2 max-md:flex-wrap">
                  <Button
                    variant="secondary"
                    leftIcon={<Download className="h-4 w-4" />}
                    onClick={handleExport}
                    disabled={(meta?.total ?? 0) === 0 || exporting}
                    loading={exporting}
                  >
                    Export
                  </Button>
                  {exportModal}
                  {hasPermission(20) && (
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Megaphone className="h-4 w-4" />}
                      onClick={() => setMarketingModalOpen(true)}
                    >
                      Email / SMS List
                    </Button>
                  )}
                  <Link to="/schedule">
                    <Button variant="secondary" leftIcon={<CalendarDays className="h-4 w-4" />}>
                      Calendar View
                    </Button>
                  </Link>
                  <Link to="/schedule">
                    <Button variant="primary" leftIcon={<CalendarPlus className="h-4 w-4" />}>
                      New Appointment
                    </Button>
                  </Link>
                </div>
              )}
            />

          </div>

          <div className="min-h-0 flex-1 flex flex-col overflow-x-hidden">
            <div className="min-h-0 flex-1">
              <DataTable<Appointment>
                data={filteredAppointments}
                columns={columns}
                keyField="appointmentId"
                loading={isLoading}
                onRowClick={handleRowClick}
                page={page}
                pageSize={perPage}
                total={meta?.total ?? 0}
                onPageChange={setPage}
                manualSort
                sortKey={sortKey}
                sortDir={sortDir}
                onSortChange={handleSortChange}
                emptyTitle="No appointments found"
                emptyDescription="Try adjusting the filters."
              />
            </div>

          </div>
        </div>
      </ListPageLayout>

      {detailAppointment && (
        <AppointmentDetailModal
          isOpen={!!detailAppointment}
          onClose={() => setDetailAppointment(null)}
          appointment={detailAppointment}
          onUpdated={() => {
            setDetailAppointment(null);
            invalidateList();
          }}
          onDeleted={() => {
            setDetailAppointment(null);
            invalidateList();
          }}
        />
      )}

      {hasPermission(20) && (
        <MarketingActionsModal
          isOpen={marketingModalOpen}
          onClose={() => setMarketingModalOpen(false)}
          sourceType="appointment"
          filterConditions={marketingFilterConditions}
        />
      )}
    </>
  );
}
